import type { QueryPlan } from '@aibi/analysis-engine';
import type {
  AIExplanationTopic,
  AnalysisState,
  Expression,
  Filter,
  QueryResult,
  TimeGrain,
} from '@aibi/contracts';
import { isCalculatedMeasure, type SemanticModelIndex } from '@aibi/semantic-model';

/** Origen verificable de cada hecho de una explicacion. */
export type ExplanationSource = 'analysis-state' | 'semantic-model' | 'query-plan' | 'query-result';

export interface ExplanationFact {
  readonly label: string;
  readonly value: string;
  readonly source: ExplanationSource;
}

export interface AnalysisExplanation {
  readonly topic: AIExplanationTopic;
  readonly title: string;
  readonly summary: string;
  readonly facts: readonly ExplanationFact[];
}

/** Metadatos del resultado admitidos en una explicacion: nunca filas ni totales. */
export type QueryResultMetadata = Pick<QueryResult, 'pagination' | 'execution' | 'warnings'>;

export interface ExplainAnalysisInput {
  readonly state: AnalysisState;
  readonly index: SemanticModelIndex;
  readonly plan?: QueryPlan | null;
  readonly result?: QueryResultMetadata | null;
  readonly measureId?: string;
}

const GRAIN_LABELS: Readonly<Record<TimeGrain, string>> = {
  hour: 'hora',
  day: 'dia',
  week: 'semana',
  month: 'mes',
  quarter: 'trimestre',
  year: 'ano',
};

const OPERATOR_LABELS: Readonly<Record<Filter['operator'], string>> = {
  eq: '=',
  neq: '<>',
  in: 'en',
  'not-in': 'no en',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  between: 'entre',
  contains: 'contiene',
  'starts-with': 'empieza con',
  'ends-with': 'termina con',
  'is-null': 'es nulo',
  'is-not-null': 'no es nulo',
};

const AGGREGATION_LABELS: Readonly<Record<string, string>> = {
  sum: 'Suma',
  avg: 'Promedio',
  min: 'Minimo',
  max: 'Maximo',
  count: 'Conteo',
  'count-distinct': 'Conteo de distintos',
  median: 'Mediana',
};

/**
 * Explicacion determinista de un analisis. Solo usa el Analysis State, el Semantic Model,
 * el QueryPlan y metadatos del QueryResult: no inventa cifras ni lee filas de datos.
 *
 * @example
 * explainAnalysis('filters', { state, index, plan }).facts; // [{ label: 'Pais', value: '= Mexico', source: 'analysis-state' }]
 */
export function explainAnalysis(
  topic: AIExplanationTopic,
  input: ExplainAnalysisInput,
): AnalysisExplanation {
  switch (topic) {
    case 'fields':
      return explainFields(input);
    case 'filters':
      return explainFilters(input);
    case 'measure':
      return explainMeasure(input);
    case 'result':
      return explainResult(input);
  }
}

const dimensionLabel = (index: SemanticModelIndex, id: string): string => {
  const dimension = index.getDimension(id);
  return dimension ? (dimension.label ?? dimension.name) : id;
};
const measureLabel = (index: SemanticModelIndex, id: string): string => {
  const measure = index.getMeasure(id);
  return measure ? (measure.label ?? measure.name) : id;
};

function fieldFacts({ state, index }: ExplainAnalysisInput): ExplanationFact[] {
  const facts: ExplanationFact[] = [];
  for (const dimension of state.dimensions) {
    const grain =
      dimension.timeGrain === undefined ? '' : ` por ${GRAIN_LABELS[dimension.timeGrain]}`;
    const description = index.getDimension(dimension.dimensionId)?.description;
    facts.push({
      label: `Dimension: ${dimensionLabel(index, dimension.dimensionId)}${grain}`,
      value: description ?? 'Agrupa el resultado por sus valores.',
      source: 'semantic-model',
    });
  }
  for (const measure of state.measures) {
    facts.push({
      label: `Medida: ${measureLabel(index, measure.measureId)}`,
      value: describeMeasure(index, measure.measureId),
      source: 'semantic-model',
    });
  }
  return facts;
}

function explainFields(input: ExplainAnalysisInput): AnalysisExplanation {
  const facts = fieldFacts(input);
  return {
    topic: 'fields',
    title: 'Campos que participan',
    summary:
      facts.length === 0
        ? 'El analisis todavia no tiene campos.'
        : `El analisis usa ${String(input.state.dimensions.length)} dimension(es) y ${String(input.state.measures.length)} medida(s).`,
    facts,
  };
}

function filterFacts({ state, index, plan }: ExplainAnalysisInput): ExplanationFact[] {
  const facts: ExplanationFact[] = state.filters.map((filter) => ({
    label:
      filter.target.kind === 'dimension'
        ? dimensionLabel(index, filter.target.dimensionId)
        : measureLabel(index, filter.target.measureId),
    value: `${OPERATOR_LABELS[filter.operator]} ${filter.values.map(String).join(', ')}`.trim(),
    source: 'analysis-state',
  }));
  for (const selection of state.selections) {
    facts.push({
      label: `Seleccion en ${dimensionLabel(index, selection.dimensionId)}`,
      value: selection.values.map(String).join(', '),
      source: 'analysis-state',
    });
  }
  for (const drill of state.drilldown) {
    const hierarchy = index.getHierarchy(drill.hierarchyId);
    facts.push({
      label: `Drill-down en ${hierarchy ? (hierarchy.label ?? hierarchy.name) : drill.hierarchyId}`,
      value: drill.path.map((step) => String(step.value)).join(' > '),
      source: 'analysis-state',
    });
  }
  const ruleIds = plan ? plan.ruleFilters.map((rule) => rule.ruleId) : [];
  for (const ruleId of [...new Set(ruleIds)]) {
    const rule = index.model.businessRules.find((candidate) => candidate.id === ruleId);
    facts.push({
      label: 'Regla de negocio',
      value: rule ? (rule.description ?? rule.name) : ruleId,
      source: 'query-plan',
    });
  }
  for (const measure of state.measures) {
    const definition = index.getMeasure(measure.measureId);
    if (definition && !isCalculatedMeasure(definition) && definition.filter) {
      facts.push({
        label: `Filtro propio de ${measureLabel(index, measure.measureId)}`,
        value: describeExpression(definition.filter, index),
        source: 'semantic-model',
      });
    }
  }
  return facts;
}

function explainFilters(input: ExplainAnalysisInput): AnalysisExplanation {
  const facts = filterFacts(input);
  return {
    topic: 'filters',
    title: 'Filtros aplicados',
    summary:
      facts.length === 0
        ? 'No hay filtros: el resultado considera todos los datos del modelo.'
        : `Se aplican ${String(facts.length)} restriccion(es) sobre los datos.`,
    facts,
  };
}

function explainMeasure(input: ExplainAnalysisInput): AnalysisExplanation {
  const { index, plan } = input;
  const measureId = input.measureId ?? input.state.measures[0]?.measureId;
  const measure = measureId === undefined ? undefined : index.getMeasure(measureId);
  if (measureId === undefined || !measure) {
    return {
      topic: 'measure',
      title: 'Calculo de la medida',
      summary: 'El analisis no tiene una medida que explicar.',
      facts: [],
    };
  }
  const facts: ExplanationFact[] = [
    { label: 'Definicion', value: describeMeasure(index, measureId), source: 'semantic-model' },
  ];
  if (measure.description !== undefined) {
    facts.push({ label: 'Descripcion', value: measure.description, source: 'semantic-model' });
  }
  const planned = plan?.measures.find((candidate) => candidate.measureId === measureId);
  if (planned && planned.dependsOnMeasureIds.length > 0) {
    facts.push({
      label: 'Depende de',
      value: planned.dependsOnMeasureIds.map((id) => measureLabel(index, id)).join(', '),
      source: 'query-plan',
    });
  }
  return {
    topic: 'measure',
    title: `Como se calcula ${measure.label ?? measure.name}`,
    summary: describeMeasure(index, measureId),
    facts,
  };
}

function explainResult(input: ExplainAnalysisInput): AnalysisExplanation {
  const { state, index, plan, result } = input;
  const facts: ExplanationFact[] = [...fieldFacts(input), ...filterFacts(input)];
  if (state.sorting.length > 0) {
    facts.push({
      label: 'Orden',
      value: state.sorting.map((sort) => `${sort.key} (${sort.direction})`).join(', '),
      source: 'analysis-state',
    });
  }
  if (state.limit !== null) {
    facts.push({
      label: 'Limite',
      value: `${String(state.limit)} filas`,
      source: 'analysis-state',
    });
  }
  if (state.comparison) {
    facts.push({
      label: 'Comparacion',
      value: `${state.comparison.kind} (${state.comparison.output})`,
      source: 'analysis-state',
    });
  }
  if (plan) {
    const tables = plan.tables
      .map((table) => {
        const semantic = index.getTable(table.id);
        return semantic ? (semantic.label ?? semantic.name) : table.id;
      })
      .join(', ');
    facts.push({ label: 'Tablas consultadas', value: tables || 'ninguna', source: 'query-plan' });
    for (const diagnostic of plan.diagnostics) {
      facts.push({ label: 'Aviso del plan', value: diagnostic.message, source: 'query-plan' });
    }
  }
  if (result) {
    const total = result.pagination.totalRows;
    facts.push({
      label: 'Filas',
      value:
        total === null
          ? `${String(result.pagination.returnedRows)} devueltas`
          : `${String(result.pagination.returnedRows)} de ${String(total)}`,
      source: 'query-result',
    });
    if (result.execution.truncated) {
      facts.push({
        label: 'Resultado truncado',
        value: 'Se alcanzo el limite de filas.',
        source: 'query-result',
      });
    }
    for (const warning of result.warnings) {
      facts.push({ label: 'Advertencia', value: warning.code, source: 'query-result' });
    }
  }
  const fields = [...state.measures.map((m) => measureLabel(index, m.measureId))].join(', ');
  const by = state.dimensions.map((d) => dimensionLabel(index, d.dimensionId)).join(', ');
  const summary =
    state.measures.length === 0 && state.dimensions.length === 0
      ? 'El analisis esta vacio: agregue dimensiones o medidas.'
      : `Ve ${fields || 'los valores'}${by === '' ? '' : ` por ${by}`}` +
        (state.filters.length === 0
          ? ' sin filtros'
          : ` con ${String(state.filters.length)} filtro(s)`) +
        ` en una visualizacion ${state.visualization.type}.`;
  return { topic: 'result', title: 'Por que ve este resultado', summary, facts };
}

function describeMeasure(index: SemanticModelIndex, measureId: string): string {
  const measure = index.getMeasure(measureId);
  if (!measure) return 'Medida no disponible.';
  if (isCalculatedMeasure(measure)) {
    return `Medida calculada: ${measure.formula ?? describeExpression(measure.expression, index)}.`;
  }
  const aggregation = AGGREGATION_LABELS[measure.aggregation] ?? measure.aggregation;
  if (measure.columnId === undefined) {
    const table = measure.tableId === undefined ? undefined : index.getTable(measure.tableId);
    return `${aggregation} de filas de ${table ? (table.label ?? table.name) : 'la tabla'}.`;
  }
  const column = index.getColumn(measure.columnId);
  const columnName = column ? (column.column.label ?? column.column.name) : measure.columnId;
  const table = column ? (column.table.label ?? column.table.name) : '';
  return `${aggregation} de ${columnName}${table === '' ? '' : ` (${table})`}.`;
}

/** Representacion legible de un AST con etiquetas del modelo (nunca SQL). */
export function describeExpression(expression: Expression, index: SemanticModelIndex): string {
  switch (expression.type) {
    case 'literal':
      return JSON.stringify(expression.value);
    case 'column': {
      const column = index.getColumn(expression.columnId);
      return column ? (column.column.label ?? column.column.name) : expression.columnId;
    }
    case 'measure':
      return `[${measureLabel(index, expression.measureId)}]`;
    case 'unary':
      return `${expression.operator.toUpperCase()} ${describeExpression(expression.operand, index)}`;
    case 'binary':
      return `(${describeExpression(expression.left, index)} ${expression.operator} ${describeExpression(expression.right, index)})`;
    case 'function':
      return `${expression.name.toUpperCase()}(${expression.args.map((arg) => describeExpression(arg, index)).join(', ')})`;
    case 'aggregate':
      return `${(AGGREGATION_LABELS[expression.function] ?? expression.function).toUpperCase()}(${
        expression.argument === undefined ? '*' : describeExpression(expression.argument, index)
      })`;
    case 'case':
      return `SI ${expression.branches
        .map(
          (branch) =>
            `${describeExpression(branch.when, index)} ENTONCES ${describeExpression(branch.then, index)}`,
        )
        .join(
          '; ',
        )}${expression.else === undefined ? '' : ` SINO ${describeExpression(expression.else, index)}`}`;
  }
}
